// src/components/Pentests/runs/ZapView.js
//
// Run-detail view for ZAP standalone recon scans. Each ScanResult row stores
// a single ZAP alert in `r.data` with the schema ZAP emits:
//   {url, name, risk, cweid, param, attack, method, wascid, evidence, solution,
//    sourceid, plugin_id, reference, confidence, description}
// Renders each alert as a card grouped by risk; the operator can filter by
// risk + by URL substring, click to expand, and copy the evidence string.

import { useMemo, useState } from 'react';
import {
  AlertOctagon, AlertTriangle, BookOpen, Bug, ChevronDown, ChevronRight,
  Copy, ExternalLink, FileJson, Filter, Info, Lightbulb, Search,
} from 'lucide-react';

const RISK_STYLES = {
  high:          { cls: 'bg-red-100 text-red-700 border-red-300',         icon: AlertOctagon,   rank: 4 },
  medium:        { cls: 'bg-orange-100 text-orange-700 border-orange-300', icon: AlertTriangle, rank: 3 },
  low:           { cls: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Info,          rank: 2 },
  informational: { cls: 'bg-slate-100 text-slate-700 border-slate-300',    icon: Info,          rank: 1 },
};

function riskKey(risk) {
  if (!risk) return 'informational';
  const k = String(risk).toLowerCase();
  if (RISK_STYLES[k]) return k;
  // ZAP sometimes emits "Informational" with a different capitalisation set.
  if (k.startsWith('info')) return 'informational';
  return 'informational';
}

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {}
      }}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 hover:text-shinobi-green transition"
      title={`${label} to clipboard`}
    >
      <Copy className="w-3 h-3" />
      {copied ? 'copied' : label}
    </button>
  );
}

function AlertCard({ result, isExpanded, onToggle }) {
  const data = result.data || {};
  const risk = riskKey(data.risk);
  const style = RISK_STYLES[risk];
  const Icon = style.icon;
  const url = data.url || result.value || '';
  const method = data.method || 'GET';
  const param = data.param || '';
  const cwe = data.cweid && data.cweid !== '-1' ? data.cweid : null;
  const cveOrPlugin = data.plugin_id || data.pluginid || data.sourceid;
  const confidence = data.confidence;
  const refs = (() => {
    const r = data.reference;
    if (!r) return [];
    if (Array.isArray(r)) return r.filter(Boolean);
    return String(r)
      .split(/\r?\n|,\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
  })();

  return (
    <div className="rounded-xl border border-cyber-300/40 bg-cyber-100/40 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-cyber-200/30 transition"
      >
        {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${style.cls} shrink-0`}>
          <Icon className="w-3 h-3" />
          {risk}
        </span>
        <span className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{data.name || '(unnamed alert)'}</div>
          <div className="text-[11px] text-slate-500 font-mono truncate">{method} {url}</div>
        </span>
        {cwe && (
          <span className="text-[10px] font-mono text-slate-500 shrink-0">CWE-{cwe}</span>
        )}
      </button>

      {isExpanded && (
        <div className="px-4 py-4 border-t border-cyber-300/20 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <Meta label="URL" value={url} mono link />
            <Meta label="Method" value={method} mono />
            {param && <Meta label="Parameter" value={param} mono />}
            {confidence && <Meta label="Confidence" value={confidence} />}
            {cweOrPluginLine(cwe, cveOrPlugin)}
          </div>

          {data.description && (
            <Section icon={Bug} label="Description">
              <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{data.description}</p>
            </Section>
          )}

          {data.attack && (
            <Section icon={AlertTriangle} label="Attack vector">
              <pre className="text-xs font-mono text-amber-700 whitespace-pre-wrap bg-cyber-100/60 border border-cyber-300/30 rounded-lg p-2">{data.attack}</pre>
            </Section>
          )}

          {data.evidence && (
            <Section
              icon={FileJson}
              label="Evidence"
              right={<CopyButton text={data.evidence} />}
            >
              <pre className="text-xs font-mono text-slate-800 whitespace-pre-wrap bg-cyber-100/60 border border-cyber-300/30 rounded-lg p-2 max-h-64 overflow-auto">{data.evidence}</pre>
            </Section>
          )}

          {data.solution && (
            <Section icon={Lightbulb} label="Solution">
              <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{data.solution}</p>
            </Section>
          )}

          {refs.length > 0 && (
            <Section icon={BookOpen} label="References">
              <ul className="space-y-1 text-xs">
                {refs.map((r, i) => (
                  <li key={i}>
                    {/^https?:\/\//.test(r) ? (
                      <a
                        href={r}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-slate-700 hover:text-shinobi-green break-all"
                      >
                        <ExternalLink className="w-3 h-3 shrink-0" />
                        {r}
                      </a>
                    ) : (
                      <span className="font-mono text-slate-500 break-all">{r}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function cweOrPluginLine(cwe, plugin) {
  const parts = [];
  if (cwe) parts.push(<Meta key="cwe" label="CWE" value={`CWE-${cwe}`} mono link={`https://cwe.mitre.org/data/definitions/${cwe}.html`} />);
  if (plugin) parts.push(<Meta key="plugin" label="ZAP plugin" value={plugin} mono />);
  return parts;
}

function Meta({ label, value, mono = false, link = null }) {
  if (!value) return null;
  const cls = `text-slate-700 ${mono ? 'font-mono break-all' : ''}`;
  let content;
  if (link === true) {
    // url-as-link
    content = (
      <a href={value} target="_blank" rel="noopener noreferrer" className={`${cls} hover:text-shinobi-green inline-flex items-center gap-1`}>
        <ExternalLink className="w-3 h-3 shrink-0" />
        {value}
      </a>
    );
  } else if (typeof link === 'string') {
    content = (
      <a href={link} target="_blank" rel="noopener noreferrer" className={`${cls} hover:text-shinobi-green inline-flex items-center gap-1`}>
        <ExternalLink className="w-3 h-3 shrink-0" />
        {value}
      </a>
    );
  } else {
    content = <span className={cls}>{value}</span>;
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      {content}
    </div>
  );
}

function Section({ icon: Icon, label, right, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
          <Icon className="w-3 h-3" />
          {label}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function ZapView({ results }) {
  const [filter, setFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());

  // Risk-summary counts (always over the full result set, not the filtered one).
  const counts = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0, informational: 0 };
    for (const r of results || []) {
      const k = riskKey(r.data?.risk);
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [results]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let xs = results || [];
    if (riskFilter !== 'all') {
      xs = xs.filter((r) => riskKey(r.data?.risk) === riskFilter);
    }
    if (q) {
      xs = xs.filter((r) => {
        const d = r.data || {};
        return [d.name, d.url, r.value, d.param, d.description, d.evidence, d.solution]
          .some((s) => s && String(s).toLowerCase().includes(q));
      });
    }
    return [...xs].sort((a, b) => {
      const ra = (RISK_STYLES[riskKey(a.data?.risk)] || {}).rank || 0;
      const rb = (RISK_STYLES[riskKey(b.data?.risk)] || {}).rank || 0;
      if (ra !== rb) return rb - ra;
      return String(a.data?.name || '').localeCompare(String(b.data?.name || ''));
    });
  }, [results, filter, riskFilter]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (!results || results.length === 0) {
    return (
      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
        <FileJson className="w-6 h-6 mx-auto mb-2 opacity-60" />
        No ZAP alerts recorded yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 relative min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name, URL, parameter, evidence..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>

        <div className="inline-flex rounded-lg border border-cyber-300/60 bg-cyber-100/60 overflow-hidden text-xs">
          {[
            { value: 'all',           label: 'All',           count: results.length },
            { value: 'high',          label: 'High',          count: counts.high },
            { value: 'medium',        label: 'Medium',        count: counts.medium },
            { value: 'low',           label: 'Low',           count: counts.low },
            { value: 'informational', label: 'Info',          count: counts.informational },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRiskFilter(opt.value)}
              className={`px-2.5 py-1.5 transition border-r last:border-r-0 border-cyber-300/60 inline-flex items-center gap-1.5 ${
                riskFilter === opt.value
                  ? 'bg-wasabi-100 text-wasabi-700'
                  : 'text-slate-700 hover:bg-cyber-200/60'
              }`}
            >
              <Filter className="w-3 h-3" />
              {opt.label}
              <span className="opacity-70">({opt.count})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-6 text-center text-sm text-slate-500">
            No alerts match the current filters.
          </div>
        ) : (
          filtered.map((r) => (
            <AlertCard
              key={r.id}
              result={r}
              isExpanded={expanded.has(r.id)}
              onToggle={() => toggle(r.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
